/**
 * Japanese (CJK) PII report as raster image via Canvas 2D — no html2canvas.
 * Uses OS fonts (Meiryo, Yu Gothic, Hiragino) so no CORS/taint and works offline.
 */

const W = 720;
const PAD = 20;
const LABEL_W = 122;
const GAP = 8;
const LINE_H = 17;
function wrapLines(ctx, text, maxW) {
  const raw = String(text ?? "");
  const paragraphs = raw.split("\n");
  const lines = [];
  for (const para of paragraphs) {
    if (para === "" && paragraphs.length === 1) {
      lines.push("");
      continue;
    }
    let cur = "";
    for (let i = 0; i < para.length; i += 1) {
      const ch = para[i];
      const test = cur + ch;
      if (ctx.measureText(test).width > maxW && cur) {
        lines.push(cur);
        cur = ch;
      } else {
        cur = test;
      }
    }
    if (cur) lines.push(cur);
  }
  return lines.length ? lines : [""];
}

/**
 * @param {object} params
 * @returns {HTMLCanvasElement}
 */
export function buildJapanesePiiPdfCanvas({
  translate,
  normalizeText,
  formatAddress,
  em,
  customerInfo,
  deviceDetails,
  row,
  tradeInDateLinePlain,
}) {
  const valueX = PAD + LABEL_W + GAP;
  const valueMaxW = W - valueX - PAD;

  const fontBody =
    '11px "Segoe UI", "Yu Gothic UI", Meiryo, "MS PGothic", "Hiragino Sans", "Hiragino Kaku Gothic ProN", sans-serif';
  const fontSection =
    'bold 13px "Segoe UI", "Yu Gothic UI", Meiryo, "MS PGothic", "Hiragino Sans", sans-serif';
  const fontLogo =
    'bold 18px "Segoe UI", Meiryo, "Hiragino Sans", "Yu Gothic UI", sans-serif';
  const fontHeaderSub = '10px "Segoe UI", Meiryo, "Yu Gothic UI", sans-serif';

  const measure = document.createElement("canvas").getContext("2d");
  measure.font = fontBody;

  const blocks = [];
  const section = (t) => blocks.push({ type: "s", text: t });
  const field = (label, value) => {
    const v =
      value === undefined || value === null || String(value).trim() === ""
        ? em
        : String(value);
    blocks.push({
      type: "f",
      label,
      lines: wrapLines(measure, v, valueMaxW),
    });
  };

  section(translate("piiPdf_personalInformation"));
  field(translate("piiPdf_name"), normalizeText(customerInfo.fullName) || em);
  const addrParts = formatAddress(customerInfo.address);
  field(
    translate("piiPdf_address"),
    addrParts.length ? addrParts.join("\n") : em
  );
  field(translate("piiPdf_occupation"), normalizeText(customerInfo.occupation));
  field(translate("piiPdf_contactNo"), normalizeText(customerInfo.contactNo));
  field(translate("piiPdf_emailAddress"), normalizeText(customerInfo.email));

  section(translate("piiPdf_tradeInInformation"));
  field(translate("piiPdf_tradeInId"), normalizeText(row.tradeInId));
  field(translate("piiPdf_tradeInDate"), tradeInDateLinePlain);

  section(translate("piiPdf_deviceInformation"));
  field(translate("piiPdf_deviceName"), normalizeText(row.device));
  field(
    translate("piiPdf_imei"),
    normalizeText(deviceDetails.imei || row.imei)
  );
  field(translate("piiPdf_manufacturer"), normalizeText(deviceDetails.manufacturer));
  field(translate("piiPdf_storage"), normalizeText(deviceDetails.storage));

  /* Match draw loop: after header rule, cy = PAD + 56 */
  let layoutY = PAD + 36 + 20;
  for (const b of blocks) {
    if (b.type === "s") {
      layoutY += 22 + 4;
    } else {
      layoutY += Math.max(LINE_H, b.lines.length * LINE_H) + 8;
    }
  }
  const H = Math.max(layoutY + 28 + PAD, 480);
  const dpr = Math.min(2, typeof window !== "undefined" ? window.devicePixelRatio || 1.5 : 2);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(W * dpr);
  canvas.height = Math.round(H * dpr);
  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);

  let cy = PAD + 18;
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#192129";
  ctx.font = fontLogo;
  ctx.fillText("Flyins", PAD, cy);

  const reportTitle = translate("piiPdf_reportTitle");
  ctx.font = fontHeaderSub;
  ctx.fillStyle = "#adb5bd";
  const tw = ctx.measureText(reportTitle).width;
  ctx.fillText(reportTitle, W - PAD - tw, cy - 2);

  cy = PAD + 36;
  ctx.strokeStyle = "#c8c8c8";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PAD, cy);
  ctx.lineTo(W - PAD, cy);
  ctx.stroke();
  cy += 20;

  for (const b of blocks) {
    if (b.type === "s") {
      ctx.font = fontSection;
      ctx.fillStyle = "#495057";
      ctx.fillText(b.text, PAD, cy + 12);
      cy += 22 + 4;
    } else {
      ctx.font = fontBody;
      ctx.fillStyle = "#6C757D";
      ctx.fillText(b.label, PAD, cy + 12);
      ctx.fillStyle = "#212529";
      let vy = cy;
      for (const line of b.lines) {
        ctx.fillText(line, valueX, vy + 12);
        vy += LINE_H;
      }
      cy = vy + 8;
    }
  }

  cy += 8;
  ctx.strokeStyle = "#c8c8c8";
  ctx.beginPath();
  ctx.moveTo(PAD, cy);
  ctx.lineTo(W - PAD, cy);
  ctx.stroke();

  return canvas;
}
